import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Edit, Trash2, MapPin, Star, Loader2, 
  Check, X, ChevronDown, Image as ImageIcon, AlertTriangle, Languages, Sparkles, Camera
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AdminArticles from '@/components/AdminArticles';
import AdminItineraries from '@/components/AdminItineraries';
import AdminEvents from '@/components/admin/AdminEvents';
import AdminTrails from '@/components/admin/AdminTrails';
import AdminPharmacies from '@/components/admin/AdminPharmacies';
import AdminWebcams from '@/components/admin/AdminWebcams';
import AdminSpots from '@/components/admin/AdminSpots';
import AdminShare, { QrDownloadButton } from '@/components/admin/AdminShare';
import AdminSections from '@/components/admin/AdminSections';
import AdminIntegrations from '@/components/admin/AdminIntegrations';
import { StatusBadge, StatusFilter, matchesStatusFilter } from '@/components/admin/StatusBadge';
import { AutomationNotice } from '@/components/admin/AutomationPanel';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const AdminPage = () => {
  const { t, getLocalizedField } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('places');
  const [places, setPlaces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [photoFilter, setPhotoFilter] = useState('all');
  const photoSource = (place) => (place.own_images?.length ? 'own' : place.images?.length ? 'google' : 'none');
  const ownPhotosCount = places.filter(p => photoSource(p) === 'own').length;
  
  // Dialog states
  const [isEditing, setIsEditing] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [placeToDelete, setPlaceToDelete] = useState(null);
  
  // Google search states
  const [googleSearchQuery, setGoogleSearchQuery] = useState('');
  const [googleResults, setGoogleResults] = useState([]);
  const [searchingGoogle, setSearchingGoogle] = useState(false);
  const [selectedGooglePlace, setSelectedGooglePlace] = useState(null);
  
  // Closed places states
  const [closedHistory, setClosedHistory] = useState([]);
  const [recentlyDeleted, setRecentlyDeleted] = useState([]);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupDone, setCleanupDone] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    name_es: '',
    name_en: '',
    name_de: '',
    description_es: '',
    bot_notes: '',
    description_en: '',
    description_de: '',
    category: '',
    categories: [],
    official_guachinche: false,
    official_guachinche_checked_at: '',
    latitude: '',
    longitude: '',
    google_place_id: '',
    google_rating: '',
    google_reviews_count: '',
    address: '',
    phone: '',
    website: '',
    instagram_url: '',
    facebook_url: '',
    tiktok_url: '',
    whatsapp: '',
    opening_hours: [],
    images: [],
    kids_friendly: false,
  });
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translatingPlace, setTranslatingPlace] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(null); // place ID being generated
  const [batchGenerating, setBatchGenerating] = useState(false);

  useEffect(() => {
    // Wait for auth loading to complete before checking access
    if (authLoading) return;
    
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    try {
      const [placesRes, catsRes] = await Promise.all([
        api.getPlaces({ limit: 200, status: 'all' }),
        api.getCategories({ include_hidden: true }),
      ]);
      setPlaces(placesRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Auto-run cleanup on admin page load (una sola vez: cleanupDone evita repetirlo)
  useEffect(() => {
    if (!loading && user?.is_admin && !cleanupDone) {
      runAutoCleanup();
    }
  }, [loading, user, cleanupDone]);

  const runAutoCleanup = async () => {
    setCleanupRunning(true);
    try {
      const [cleanupRes, historyRes] = await Promise.all([
        api.autoCleanupClosedPlaces(),
        api.closedPlacesHistory(),
      ]);
      
      const deleted = cleanupRes.data.deleted || [];
      setRecentlyDeleted(deleted);
      setClosedHistory(historyRes.data.history || []);
      setCleanupDone(true);
      
      if (deleted.length > 0) {
        toast.warning(`${deleted.length} lugar(es) cerrado(s) eliminado(s) automaticamente`);
        // Reload places list since some were deleted
        const placesRes = await api.getPlaces({ limit: 200, status: 'all' });
        setPlaces(placesRes.data);
      }
    } catch (error) {
      console.error('Error in auto cleanup:', error);
    } finally {
      setCleanupRunning(false);
    }
  };

  const handleGoogleSearch = async () => {
    if (!googleSearchQuery.trim()) return;
    
    setSearchingGoogle(true);
    try {
      const res = await api.searchGooglePlaces(googleSearchQuery);
      setGoogleResults(res.data);
      if (res.data.length === 0) {
        toast.info('No se encontraron resultados');
      }
    } catch (error) {
      console.error('Error searching Google:', error);
      toast.error('Error al buscar en Google Maps');
    } finally {
      setSearchingGoogle(false);
    }
  };

  const importFromGoogle = (googlePlace) => {
    setSelectedGooglePlace(googlePlace);
    setFormData(prev => ({
      ...prev,
      name_es: googlePlace.name,
      name_en: googlePlace.name,
      name_de: googlePlace.name,
      latitude: googlePlace.latitude,
      longitude: googlePlace.longitude,
      google_place_id: googlePlace.place_id,
      google_rating: googlePlace.rating || '',
      google_reviews_count: googlePlace.reviews_count || '',
      address: googlePlace.address || '',
      phone: googlePlace.phone || '',
      website: googlePlace.website || '',
      instagram_url: '',
    facebook_url: '',
    tiktok_url: '',
    whatsapp: '',
      opening_hours: googlePlace.opening_hours || [],
      images: googlePlace.photos || [],
    }));
    setGoogleResults([]);
    setGoogleSearchQuery('');
    setEditingPlace(null); // Es un lugar nuevo
    setIsEditing(true); // Abrir el editor automáticamente
    toast.success('Datos importados - Completa los campos y guarda');
  };

  const handleOpenEditor = (place = null) => {
    if (place) {
      setEditingPlace(place);
      setFormData({
        name_es: place.name_es || '',
        name_en: place.name_en || '',
        name_de: place.name_de || '',
        description_es: place.description_es || '',
        bot_notes: place.bot_notes || '',
        description_en: place.description_en || '',
        description_de: place.description_de || '',
        category: place.category || '',
        categories: place.categories?.length ? place.categories : (place.category ? [place.category] : []),
        official_guachinche: !!place.official_guachinche,
        official_guachinche_checked_at: place.official_guachinche_checked_at || '',
        latitude: place.latitude || '',
        longitude: place.longitude || '',
        google_place_id: place.google_place_id || '',
        google_rating: place.google_rating || '',
        google_reviews_count: place.google_reviews_count || '',
        address: place.address || '',
        phone: place.phone || '',
        website: place.website || '',
        instagram_url: place.instagram_url || '',
        facebook_url: place.facebook_url || '',
        tiktok_url: place.tiktok_url || '',
        whatsapp: place.whatsapp || '',
        opening_hours: place.opening_hours || [],
        images: place.images || [],
        kids_friendly: place.kids_friendly || false,
      });
    } else {
      setEditingPlace(null);
      setFormData({
        name_es: '',
        name_en: '',
        name_de: '',
        description_es: '',
    bot_notes: '',
        description_en: '',
        description_de: '',
        category: '',
        categories: [],
        official_guachinche: false,
        official_guachinche_checked_at: '',
        latitude: '',
        longitude: '',
        google_place_id: '',
        google_rating: '',
        google_reviews_count: '',
        address: '',
        phone: '',
        website: '',
        instagram_url: '',
    facebook_url: '',
    tiktok_url: '',
    whatsapp: '',
        opening_hours: [],
        images: [],
        kids_friendly: false,
      });
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!formData.name_es || !formData.category || !formData.latitude || !formData.longitude) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        google_rating: formData.google_rating ? parseFloat(formData.google_rating) : null,
        google_reviews_count: formData.google_reviews_count ? parseInt(formData.google_reviews_count) : null,
        official_guachinche_checked_at: formData.official_guachinche && formData.official_guachinche_checked_at ? formData.official_guachinche_checked_at : null,
      };

      if (editingPlace) {
        await api.updatePlace(editingPlace.id, data);
        toast.success('Lugar actualizado');
      } else {
        await api.createPlace(data);
        toast.success('Lugar creado');
      }
      
      setIsEditing(false);
      loadData();
    } catch (error) {
      console.error('Error saving place:', error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!placeToDelete) return;
    
    try {
      await api.deletePlace(placeToDelete.id);
      toast.success('Lugar eliminado');
      setShowDeleteDialog(false);
      setPlaceToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting place:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleRetranslateAll = async () => {
    setTranslating(true);
    try {
      const res = await api.retranslateAll();
      const d = res.data.details;
      toast.success(
        `Traducido: ${d.places} lugares, ${d.articles} articulos, ${d.events} eventos, ${d.itineraries} itinerarios`
      );
      if (d.errors?.length > 0) {
        toast.warning(`${d.errors.length} error(es) durante la traduccion`);
      }
    } catch (error) {
      console.error('Error retranslating:', error);
      toast.error('Error al re-traducir');
    } finally {
      setTranslating(false);
    }
  };

  const handleTranslatePlace = async (placeId) => {
    setTranslatingPlace(placeId);
    try {
      const res = await api.translatePlace(placeId);
      if (res.data.success) {
        toast.success(`Lugar traducido (${res.data.translated_fields.length} campos)`);
        loadData();
      } else {
        toast.warning(res.data.message || 'No se pudo traducir');
      }
    } catch (error) {
      console.error('Error translating place:', error);
      toast.error('Error al traducir: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTranslatingPlace(null);
    }
  };

  const handleTranslateAllPlaces = async () => {
    const untranslated = places.filter(p => !p.name_en || !p.name_de);
    if (untranslated.length === 0) {
      toast.info('Todos los lugares ya estan traducidos');
      return;
    }
    if (!window.confirm(`¿Traducir ${untranslated.length} lugares sin traducir?`)) return;
    
    setTranslatingPlace('all');
    try {
      const res = await api.translateAllPlaces();
      if (res.data.success) {
        toast.success(`Traducidos ${res.data.translated} lugares`);
        loadData();
      }
    } catch (error) {
      console.error('Batch translation error:', error);
      toast.error('Error al traducir: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTranslatingPlace(null);
    }
  };

  // Count untranslated places
  const untranslatedPlacesCount = places.filter(p => !p.name_en || !p.name_de).length;

  const handleGenerateAISummary = async (placeId, instagramUrl) => {
    setGeneratingAI(placeId);
    try {
      const res = await api.generatePlaceSummary(placeId, instagramUrl);
      toast.success(`Resumen generado para ${res.data.place_name}`);
      loadData();
    } catch (error) {
      console.error('Error generating summary:', error);
      toast.error(error.response?.data?.detail || 'Error al generar resumen IA');
    } finally {
      setGeneratingAI(null);
    }
  };

  const handleBatchGenerate = async () => {
    setBatchGenerating(true);
    try {
      await api.generateAllSummaries();
      toast.info('Generacion masiva iniciada. Puede tardar varios minutos...');
      // Poll for status
      const poll = setInterval(async () => {
        try {
          const res = await api.getSummaryStatus();
          const batch = res.data.latest_batch;
          if (batch?.status === 'running') {
            const d = batch.details;
            toast.info(`Progreso: ${d.processed}/${d.total} lugares procesados`, { id: 'batch-progress' });
          } else {
            clearInterval(poll);
            setBatchGenerating(false);
            if (batch?.status === 'success') {
              toast.success(`Resumenes generados: ${batch.details.processed} lugares`, { id: 'batch-progress' });
              loadData();
            }
          }
        } catch { clearInterval(poll); setBatchGenerating(false); }
      }, 10000);
    } catch (error) {
      setBatchGenerating(false);
      if (error.response?.status === 409) {
        toast.warning('Ya hay una generacion en curso');
      } else {
        toast.error('Error al iniciar generacion masiva');
      }
    }
  };


  const filteredPlaces = places.filter(place => {
    if (!matchesStatusFilter(place, statusFilter)) return false;
    if (photoFilter !== 'all' && photoSource(place) !== photoFilter) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      place.name_es?.toLowerCase().includes(query) ||
      place.name_en?.toLowerCase().includes(query) ||
      place.address?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-64 mb-6" />
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 min-h-screen bg-[#FDFBF7]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1D1D1F]">
              {t('adminPanel')}
            </h1>
          </div>
          <Button
            onClick={handleRetranslateAll}
            disabled={translating}
            variant="outline"
            className="border-[#38B2AC] text-[#38B2AC] hover:bg-[#38B2AC] hover:text-white rounded-full"
            data-testid="retranslate-all-btn"
          >
            {translating ? (
              <Loader2 className="animate-spin mr-2" size={18} />
            ) : (
              <Languages className="mr-2" size={18} />
            )}
            {translating ? 'Traduciendo...' : 'Re-traducir todo'}
          </Button>
          <Button
            onClick={handleBatchGenerate}
            disabled={batchGenerating}
            variant="outline"
            className="border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white rounded-full"
            data-testid="batch-ai-btn"
          >
            {batchGenerating ? (
              <Loader2 className="animate-spin mr-2" size={18} />
            ) : (
              <Sparkles className="mr-2" size={18} />
            )}
            {batchGenerating ? 'Generando...' : 'Resumenes IA (todos)'}
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 overflow-x-auto" data-testid="admin-tabs">
          {[
            { key: 'places', label: `Lugares (${places.length})` },
            { key: 'trails', label: 'Senderos' },
            { key: 'events', label: 'Eventos' },
            { key: 'pharmacies', label: 'Farmacias' },
            { key: 'weather', label: 'Tiempo' },
            { key: 'share', label: 'Compartidos' },
            { key: 'itineraries', label: 'Itinerarios' },
            { key: 'articles', label: 'Articulos' },
            { key: 'sections', label: 'Secciones' },
            { key: 'integrations', label: 'Integraciones' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2.5 px-3 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-white text-[#1D1D1F] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Trails */}
        {activeTab === 'trails' && <AdminTrails />}

        {/* Tab: Events */}
        {activeTab === 'events' && (<><AutomationNotice kind="events" /><AdminEvents /></>)}

        {/* Tab: Pharmacies */}
        {activeTab === 'pharmacies' && (<><AutomationNotice kind="pharmacies" /><AdminPharmacies /></>)}
        {activeTab === 'weather' && (<><AdminWebcams /><AdminSpots /></>)}
        {activeTab === 'share' && <AdminShare />}

        {/* Tab: Articles */}
        {activeTab === 'articles' && <AdminArticles />}

        {/* Tab: Sections Visibility */}
        {activeTab === 'sections' && <AdminSections />}

        {/* Tab: Integrations (API keys for Guía Publisher) */}
        {activeTab === 'integrations' && <AdminIntegrations />}

        {/* Tab: Itineraries */}
        {activeTab === 'itineraries' && <AdminItineraries places={places} />}

        {/* Tab: Places */}
        {activeTab === 'places' && (<>
          <AutomationNotice kind="photos" />
        <div className="flex justify-end mb-4">
          <Button
            onClick={() => handleOpenEditor()}
            className="bg-[#38B2AC] hover:bg-[#005A63] rounded-full"
            data-testid="add-place-btn"
          >
            <Plus size={18} className="mr-2" />
            {t('addPlace')}
          </Button>
        </div>

        {/* Auto-cleanup notification + history */}
        {cleanupRunning && (
          <div className="flex items-center gap-3 bg-white rounded-2xl p-4 shadow-sm mb-6 border border-gray-100" data-testid="cleanup-running">
            <Loader2 className="animate-spin text-[#38B2AC]" size={20} />
            <p className="text-sm text-gray-600">Verificando estado de lugares en Google Maps...</p>
          </div>
        )}

        {recentlyDeleted.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6" data-testid="auto-deleted-alert">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="text-red-500" size={20} />
              <h3 className="font-semibold text-red-800">
                {recentlyDeleted.length} lugar(es) eliminado(s) automaticamente
              </h3>
            </div>
            <p className="text-sm text-red-600 mb-3">
              Google Maps confirma que estos negocios han cerrado permanentemente. Se han eliminado de la app:
            </p>
            <div className="space-y-2">
              {recentlyDeleted.map((place, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-white/70 rounded-xl p-3">
                  <X size={16} className="text-red-400 shrink-0" />
                  <div>
                    <p className="font-medium text-sm text-gray-900">{place.name_es}</p>
                    <p className="text-xs text-gray-500">{place.address}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {closedHistory.length > 0 && (
          <details className="bg-white rounded-2xl shadow-sm mb-6 border border-gray-100 group" data-testid="closed-history">
            <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-amber-500" size={18} />
                <span className="font-semibold text-sm text-[#1D1D1F]">
                  Historial de lugares eliminados ({closedHistory.length})
                </span>
              </div>
              <ChevronDown size={18} className="text-gray-400 transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-5 pb-5 space-y-2">
              {closedHistory.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    {entry.image && (
                      <img src={entry.image} alt="" className="w-10 h-10 object-cover" />
                    )}
                    <div>
                      <p className="font-medium text-sm text-gray-900">{entry.name_es}</p>
                      <p className="text-xs text-gray-500">{entry.address}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {new Date(entry.deleted_at).toLocaleDateString('es-ES')}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Google Search */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <MapPin className="text-[#38B2AC]" size={20} />
            {t('searchGoogleMaps')}
          </h2>
          <div className="flex gap-2">
            <Input
              placeholder="Buscar lugar en Tenerife..."
              value={googleSearchQuery}
              onChange={(e) => setGoogleSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleGoogleSearch()}
              className="flex-1 rounded-xl"
              data-testid="google-search-input"
            />
            <Button
              onClick={handleGoogleSearch}
              disabled={searchingGoogle}
              className="bg-[#38B2AC] hover:bg-[#005A63] rounded-xl"
              data-testid="google-search-btn"
            >
              {searchingGoogle ? (
                <Loader2 className="animate-spin\" size={18} />
              ) : (
                <Search size={18} />
              )}
            </Button>
          </div>

          {/* Google Results */}
          {googleResults.length > 0 && (
            <div className="mt-4 space-y-3">
              {googleResults.map((result) => (
                <div
                  key={result.place_id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {result.photos?.[0] && (
                      <img
                        src={result.photos[0]}
                        alt={result.name}
                        className="w-16 h-16 object-cover"
                      />
                    )}
                    <div>
                      <h3 className="font-semibold">{result.name}</h3>
                      <p className="text-sm text-gray-500">{result.address}</p>
                      {result.rating && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star size={14} className="text-yellow-500" fill="currentColor" />
                          <span className="text-sm">{result.rating}</span>
                          {result.reviews_count && (
                            <span className="text-xs text-gray-400">({result.reviews_count})</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => importFromGoogle(result)}
                    variant="outline"
                    className="rounded-full"
                    data-testid={`import-btn-${result.place_id}`}
                  >
                    {t('importFromGoogle')}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search places */}
        <div className="flex gap-3 items-center mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Buscar lugares..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white rounded-xl"
              data-testid="places-search-input"
            />
          </div>
          <StatusFilter value={statusFilter} onChange={setStatusFilter} testId="places-status-filter" />
          <select
            value={photoFilter}
            onChange={(e) => setPhotoFilter(e.target.value)}
            className="h-9 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700"
            data-testid="places-photo-filter"
          >
            <option value="all">Todas las fotos</option>
            <option value="own">Fotos propias ({ownPhotosCount})</option>
            <option value="google">Fotos de Google ({places.filter(p => photoSource(p) === 'google').length})</option>
            <option value="none">Sin fotos ({places.filter(p => photoSource(p) === 'none').length})</option>
          </select>
          {untranslatedPlacesCount > 0 && (
            <Button 
              onClick={handleTranslateAllPlaces} 
              disabled={translatingPlace === 'all'}
              className="bg-blue-500 hover:bg-blue-600 rounded-full text-sm whitespace-nowrap" 
              data-testid="translate-all-places-btn"
            >
              {translatingPlace === 'all' ? <Loader2 className="animate-spin mr-1" size={14} /> : <Languages size={14} className="mr-1" />}
              Traducir {untranslatedPlacesCount}
            </Button>
          )}
        </div>

        {/* Places list */}
        <div className="space-y-3">
          {filteredPlaces.map((place) => (
            <div
              key={place.id}
              className="bg-white rounded-xl p-4 shadow-sm flex items-center justify-between"
              data-testid={`place-row-${place.id}`}
            >
              <div className="flex items-center gap-4">
                <img
                  src={place.images?.[0] || 'https://via.placeholder.com/80'}
                  alt={place.name_es}
                  className="w-20 h-20 object-cover"
                />
                <div>
                  <h3 className="font-semibold text-[#1D1D1F] flex items-center gap-2">{place.name_es} <StatusBadge item={place} resource="places" onChanged={loadData} /></h3>
                  <p className="text-sm text-gray-500">{place.address}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs bg-[#38B2AC]/10 text-[#38B2AC] px-2 py-1 rounded-full">
                      {categories.find(c => c.slug === place.category)?.name_es || place.category}
                    </span>
                    {photoSource(place) === 'own' ? (
                      <span className="flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full" title="Fotos subidas por Grokbot" data-testid={`photo-source-${place.id}`}>
                        <Camera size={12} /> Propias ({place.own_images.length})
                      </span>
                    ) : photoSource(place) === 'google' ? (
                      <span className="flex items-center gap-1 text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full" title="Fotos de Google Places (caducan)" data-testid={`photo-source-${place.id}`}>
                        <Camera size={12} /> Google
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs bg-red-50 text-red-500 px-2 py-1 rounded-full" data-testid={`photo-source-${place.id}`}>
                        <Camera size={12} /> Sin fotos
                      </span>
                    )}
                    {place.google_rating && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Star size={12} className="text-yellow-500" fill="currentColor" />
                        {place.google_rating}
                      </span>
                    )}
                    {place.ai_summary_generated && (
                      <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        <Sparkles size={10} />
                        IA
                      </span>
                    )}
                    {place.kids_friendly && (
                      <span className="flex items-center gap-1 text-xs text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full">
                        👶 Kids
                      </span>
                    )}
                    {(!place.name_en || !place.name_de) && (
                      <span className="text-xs text-amber-500">Sin traducir</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleTranslatePlace(place.id)}
                  disabled={translatingPlace === place.id}
                  title="Traducir a EN/DE"
                  data-testid={`translate-btn-${place.id}`}
                >
                  {translatingPlace === place.id ? (
                    <Loader2 size={18} className="animate-spin text-blue-500" />
                  ) : (
                    <Languages size={18} className="text-blue-500" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleGenerateAISummary(place.id, place.instagram_url)}
                  disabled={generatingAI === place.id}
                  title="Generar resumen IA"
                  data-testid={`ai-btn-${place.id}`}
                >
                  {generatingAI === place.id ? (
                    <Loader2 size={18} className="animate-spin text-purple-500" />
                  ) : (
                    <Sparkles size={18} className="text-purple-500" />
                  )}
                </Button>
                <QrDownloadButton kind="p" itemId={place.id} name={place.name_es} />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenEditor(place)}
                  data-testid={`edit-btn-${place.id}`}
                >
                  <Edit size={18} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => {
                    setPlaceToDelete(place);
                    setShowDeleteDialog(true);
                  }}
                  data-testid={`delete-btn-${place.id}`}
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            </div>
          ))}
        </div>

      {/* Edit/Create Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white border shadow-xl">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-xl font-bold text-gray-900">
              {editingPlace ? t('editPlace') : t('addPlace')}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {/* Names */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Nombre (Español) *</Label>
              <Input
                value={formData.name_es}
                onChange={(e) => setFormData(prev => ({ ...prev, name_es: e.target.value }))}
                placeholder="Nombre en español"
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-name-es"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Name (English) *</Label>
              <Input
                value={formData.name_en}
                onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                placeholder="Name in English"
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-name-en"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Name (Deutsch)</Label>
              <Input
                value={formData.name_de}
                onChange={(e) => setFormData(prev => ({ ...prev, name_de: e.target.value }))}
                placeholder="Name auf Deutsch"
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-name-de"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-gray-700 font-medium">Categorías * <span className="font-normal text-gray-400">(marca varias; la principal va en teal)</span></Label>
              <div className="flex flex-wrap gap-2" data-testid="select-categories">
                {categories.map((cat) => {
                  const list = formData.categories || [];
                  const on = list.includes(cat.slug);
                  const main = list[0] === cat.slug;
                  return (
                    <div key={cat.id} className={`inline-flex items-stretch border text-sm ${main ? 'border-[#38B2AC] bg-[#38B2AC] text-white' : on ? 'border-[#38B2AC] text-[#1D1D1F] bg-[#38B2AC]/10' : 'border-gray-200 text-gray-600 bg-white'}`}>
                      <button type="button" onClick={() => setFormData(prev => {
                        const cur = prev.categories || [];
                        const next = cur.includes(cat.slug) ? cur.filter(s => s !== cat.slug) : [...cur, cat.slug];
                        return { ...prev, categories: next, category: next[0] || '' };
                      })} className="px-3 py-1.5" data-testid={`cat-toggle-${cat.slug}`}>{cat.name_es}</button>
                      {on && !main && (
                        <button type="button" title="Marcar como principal" onClick={() => setFormData(prev => ({ ...prev, categories: [cat.slug, ...(prev.categories || []).filter(s => s !== cat.slug)], category: cat.slug }))}
                          className="px-2 border-l border-[#38B2AC]/40 text-[#38B2AC] hover:bg-[#38B2AC]/20" data-testid={`cat-main-${cat.slug}`}>★</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2 md:col-span-2">
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input type="checkbox" checked={!!formData.official_guachinche} onChange={(e) => setFormData(prev => ({ ...prev, official_guachinche: e.target.checked }))} className="w-4 h-4 accent-[#7B2D3A]" data-testid="toggle-official-guachinche" />
                  <span className="text-[#7B2D3A]">Guachinche oficial</span> <span className="text-gray-400 font-normal">(registro del Cabildo)</span>
                </label>
                {formData.official_guachinche && (
                  <label className="flex items-center gap-2 text-sm">Comprobado el
                    <input type="date" value={formData.official_guachinche_checked_at || ''} onChange={(e) => setFormData(prev => ({ ...prev, official_guachinche_checked_at: e.target.value }))} className="h-9 border border-gray-300 rounded-md px-2 text-sm" data-testid="official-guachinche-date" />
                  </label>
                )}
              </div>
            </div>
            <div className="space-y-2 hidden">
              <Label className="text-gray-700 font-medium">Categoría *</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#38B2AC]"
                data-testid="select-category"
              >
                <option value="">Seleccionar categoría</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.slug}>
                    {cat.name_es}
                  </option>
                ))}
              </select>
            </div>

            {/* Coordinates */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Latitud *</Label>
              <Input
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                placeholder="28.2916"
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-latitude"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Longitud *</Label>
              <Input
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                placeholder="-16.6291"
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-longitude"
              />
            </div>

            {/* Descriptions */}
            <div className="space-y-2 md:col-span-2">
              <Label className="text-gray-700 font-medium">Descripción (Español)</Label>
              <Textarea
                value={formData.description_es}
                onChange={(e) => setFormData(prev => ({ ...prev, description_es: e.target.value }))}
                placeholder="Descripción en español"
                rows={3}
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-description-es"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-gray-700 font-medium">Description (English)</Label>
              <Textarea
                value={formData.description_en}
                onChange={(e) => setFormData(prev => ({ ...prev, description_en: e.target.value }))}
                placeholder="Description in English"
                rows={3}
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-description-en"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-gray-700 font-medium">Notas internas para el bot <span className="text-xs font-normal text-gray-400">(no las ve el turista · Markdown)</span></Label>
              <Textarea
                value={formData.bot_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, bot_notes: e.target.value }))}
                placeholder="Reservas, aparcamiento, qué pedir, trucos locales, fuentes…"
                rows={3}
                className="bg-amber-50/50 border-amber-200 text-gray-900 placeholder:text-gray-400"
                data-testid="input-bot-notes"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-gray-700 font-medium">Beschreibung (Deutsch)</Label>
              <Textarea
                value={formData.description_de}
                onChange={(e) => setFormData(prev => ({ ...prev, description_de: e.target.value }))}
                placeholder="Beschreibung auf Deutsch"
                rows={3}
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-description-de"
              />
            </div>

            {/* Additional info */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Dirección</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Dirección completa"
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-address"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Teléfono</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+34 922 000 000"
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-phone"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Sitio web</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://..."
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-website"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Instagram URL</Label>
              <Input
                value={formData.instagram_url}
                onChange={(e) => setFormData(prev => ({ ...prev, instagram_url: e.target.value }))}
                placeholder="https://www.instagram.com/p/..."
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-instagram-url"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Facebook URL</Label>
              <Input value={formData.facebook_url} onChange={(e) => setFormData(prev => ({ ...prev, facebook_url: e.target.value }))} placeholder="https://facebook.com/..." className="bg-gray-50 border-gray-300 text-gray-900" data-testid="input-facebook-url" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">TikTok URL</Label>
              <Input value={formData.tiktok_url} onChange={(e) => setFormData(prev => ({ ...prev, tiktok_url: e.target.value }))} placeholder="https://tiktok.com/@..." className="bg-gray-50 border-gray-300 text-gray-900" data-testid="input-tiktok-url" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">WhatsApp</Label>
              <Input value={formData.whatsapp} onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))} placeholder="34600000000 o https://wa.me/..." className="bg-gray-50 border-gray-300 text-gray-900" data-testid="input-whatsapp" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Google Rating</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={formData.google_rating}
                onChange={(e) => setFormData(prev => ({ ...prev, google_rating: e.target.value }))}
                placeholder="4.5"
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-rating"
              />
            </div>

            {/* Kids Friendly Toggle */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Kids Friendly</Label>
              <div 
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  formData.kids_friendly ? 'bg-teal-50 border border-teal-200' : 'bg-gray-50 border border-gray-200'
                }`}
                onClick={() => setFormData(prev => ({ ...prev, kids_friendly: !prev.kids_friendly }))}
                data-testid="toggle-kids-friendly"
              >
                <div className={`w-10 h-6 rounded-full transition-colors flex items-center px-1 ${
                  formData.kids_friendly ? 'bg-[#38B2AC] justify-end' : 'bg-gray-300 justify-start'
                }`}>
                  <div className="w-4 h-4 bg-white rounded-full shadow" />
                </div>
                <span className={`text-sm font-medium ${formData.kids_friendly ? 'text-teal-700' : 'text-gray-500'}`}>
                  {formData.kids_friendly ? 'Apto para niños' : 'No especificado'}
                </span>
              </div>
            </div>

            {/* Images */}
            <div className="space-y-2 md:col-span-2">
              <Label className="text-gray-700 font-medium">URLs de imágenes (una por línea)</Label>
              <Textarea
                value={formData.images?.join('\n') || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  images: e.target.value.split('\n').filter(url => url.trim()) 
                }))}
                placeholder="https://imagen1.jpg&#10;https://imagen2.jpg"
                rows={3}
                className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                data-testid="input-images"
              />
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="border-gray-300 text-gray-700"
              data-testid="cancel-btn"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#38B2AC] hover:bg-[#005A63]"
              data-testid="save-btn"
            >
              {saving ? (
                <Loader2 className="animate-spin mr-2" size={18} />
              ) : (
                <Check className="mr-2" size={18} />
              )}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deletePlace')}</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres eliminar "{placeToDelete?.name_es}"? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </>)}
      </div>
    </div>
  );
};

export default AdminPage;
